/**
 * Agents Assistant application shell.
 *
 * Each view is a URL. `/chat`, `/history`, `/drafts`, and `/` redirects to the first.
 *
 * This used to be `useState`, on the argument that three views with no deep links did not justify a
 * router. That argument was wrong about what the URLs are for. They are not for sharing — every view
 * is scoped to the employee looking at it — they are for the browser: back and forward, refresh
 * landing where you were, and opening the history in a second tab while a long answer streams in the
 * first. An internal tool that people keep open all day beside three others has to behave like a
 * page. See `shared/src/router/router.tsx` for why the router is written rather than installed.
 *
 * ## The side rail
 *
 * From `design/new design/legacy/bank_ai_assistant_chat`: navigation lives in a persistent rail, not
 * a top bar, with the brand lockup at its head and a card at its foot carrying the accuracy
 * disclaimer, the confidentiality marking, the language toggle and sign-out.
 *
 * The new charte makes this rail and the back office's identical in tone, where they used to differ
 * — see the note in `SideNav` about what that costs.
 *
 * The mockup's rail also lists *past conversations* grouped by recency. That needs the selected
 * session lifted out of `ChatPage` and into this component, which is a change to the chat surface
 * rather than to the shell, so it is deliberately not done here — see the note on `ChatPage`.
 *
 * ## The employee id comes from the session, not from a constant
 *
 * It used to be `DEVELOPMENT_EMPLOYEE_ID`, threaded into each page and sent as a header the server
 * trusted in development mode. It is now whoever signed in, and the header is not sent at all — the
 * session cookie carries the identity and the server derives it from a signed token. The pages take it
 * as a prop only so they can scope *their own* fetches; nothing about what they are allowed to read is
 * decided here.
 *
 * Every agent sees the same three views: an agent's role admits them to all of the assistant and none
 * of the back office, which is a server-side fact (`require_admin` on every back-office route) rather
 * than something this navigation implements.
 */

import { useTranslation } from "react-i18next";

import {
  AppShell,
  AuthGate,
  BrandMark,
  ChatIcon,
  DraftsIcon,
  HistoryIcon,
  RailFooter,
  SessionMenu,
  SessionProvider,
  SpeechProvider,
  SideNav,
  SideNavItem,
  useRoute,
  ToastProvider,
  useSession,
} from "@nab/shared";

import { ChatPage } from "./pages/ChatPage";
import { DraftsPage } from "./pages/DraftsPage";
import { HistoryPage } from "./pages/HistoryPage";

const ROUTES = [
  { id: "chat", path: "/chat" },
  { id: "history", path: "/history" },
  { id: "drafts", path: "/drafts" },
] as const;

type Page = (typeof ROUTES)[number]["id"];

const ICONS: Record<Page, JSX.Element> = {
  chat: <ChatIcon />,
  history: <HistoryIcon />,
  drafts: <DraftsIcon />,
};

export function App(): JSX.Element {
  const { t } = useTranslation("app");

  return (
    <SessionProvider>
      <ToastProvider regionLabel={t("toast.region")} closeLabel={t("toast.close")}>
        <AuthGate title={t("app.name")}>
          {/* Inside the gate: /speech/status needs a session, and asking before one exists would
              answer 401 and render an interface with no voice for the rest of the page's life. */}
          <SpeechProvider>
            <Assistant />
          </SpeechProvider>
        </AuthGate>
      </ToastProvider>
    </SessionProvider>
  );
}

function Assistant(): JSX.Element {
  const { t } = useTranslation("app");
  const { identity } = useSession();
  // `/` and anything unrecognised land on the chat, replacing history rather than pushing — an
  // employee who opens a stale bookmark should not have to press back twice to leave a route they
  // never chose to visit.
  const page = useRoute(ROUTES, "chat");

  // `AuthGate` only renders this once a session resolved, so the identity is present. The fallback
  // keeps the prop's type honest rather than asserting non-null.
  const employeeId = identity?.employee_id ?? "";

  // The chat owns the whole viewport.
  //
  // Its rail is the employee's conversation history, not navigation, and the design gives it the
  // full width — three columns with their own breakpoints. Wrapping that in a shell whose rail is
  // a nav menu would put two rails side by side and leave neither with room. History and drafts
  // are ordinary screens and keep the shell.
  if (page === "chat") {
    return <ChatPage employeeId={employeeId} />;
  }

  return (
    <AppShell
      title={t("app.name")}
      heading={t(`nav.${page}`)}
      actions={<SessionMenu />}
      sidebar={
        <SideNav
          label={t("a11y.mainNavigation", { ns: "common" })}
          brand={<BrandMark label={t("brand.bank", { ns: "common" })} />}
          // The language toggle lives at the foot of the rail, as in the mockup. An employee who
          // needs the interface in Arabic needs it before they read anything else, so it is reachable
          // from every screen rather than buried in a settings page.
          footer={<RailFooter />}
        >
          {ROUTES.map((route) => (
            <SideNavItem
              key={route.id}
              icon={ICONS[route.id]}
              href={route.path}
              active={route.id === page}
            >
              {t(`nav.${route.id}`)}
            </SideNavItem>
          ))}
        </SideNav>
      }
    >
      {page === "history" ? <HistoryPage employeeId={employeeId} /> : null}
      {page === "drafts" ? <DraftsPage employeeId={employeeId} /> : null}
    </AppShell>
  );
}
